import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { MainLayout } from '@/presentation/layouts/MainLayout';
import { LoginPage } from '@/presentation/pages/LoginPage';
import { DashboardPage } from '@/presentation/pages/DashboardPage';
import { TenantsPage } from '@/presentation/pages/TenantsPage';
import { PlansPage } from '@/presentation/pages/PlansPage';
import { AdicionalesPage } from '@/presentation/pages/AdicionalesPage';
import { CoofitConexionPage } from '@/presentation/pages/CoofitConexionPage';
import { CoofitMarketingPage } from '@/presentation/pages/CoofitMarketingPage';
import { ComercialLayout } from '@/presentation/pages/ComercialLayout';
import { BillingCurrentPeriodPage, BillingHistoryPage } from '@/presentation/pages/BillingPanelPage';
import { ReportsPage } from '@/presentation/pages/ReportsPage';
import { PeriodOpsPage } from '@/presentation/pages/PeriodOpsPage';
import { PeriodSettingsPage } from '@/presentation/pages/PeriodSettingsPage';
import { CollectionsPage } from '@/presentation/pages/CollectionsPage';
import { EcosystemUsagePage } from '@/presentation/pages/EcosystemUsagePage';
import { ClinicInvoicesPage } from '@/presentation/pages/ClinicInvoicesPage';
import { ReminderAnalyticsPage } from '@/presentation/pages/ReminderAnalyticsPage';
import { SupportPage } from '@/presentation/pages/SupportPage';
import { EmailsPage } from '@/presentation/pages/EmailsPage';
import { DocumentationPage } from '@/presentation/pages/DocumentationPage';
import { DocumentationArticlePage } from '@/presentation/pages/DocumentationArticlePage';
import { SolicitudesPage } from '@/presentation/pages/SolicitudesPage';
import { SettingsPage } from '@/presentation/pages/SettingsPage';
import { EmailTemplatesPage } from '@/presentation/pages/EmailTemplatesPage';
import { ExpenseCategoriesPage } from '@/presentation/pages/ExpenseCategoriesPage';
import { CostProjectionPage } from '@/presentation/pages/CostProjectionPage';
import { LogsPage } from '@/presentation/pages/LogsPage';
import { AuditoriasPage } from '@/presentation/pages/AuditoriasPage';
import { PanelUsersPage } from '@/presentation/pages/PanelUsersPage';
import { PaymentSuccessPage } from '@/presentation/pages/PaymentSuccessPage';
import { PaymentErrorPage } from '@/presentation/pages/PaymentErrorPage';
import { PaymentPendingPage } from '@/presentation/pages/PaymentPendingPage';
import { ProtectedRoute } from '@/presentation/components/common/ProtectedRoute';

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/pago/ok',
    element: <PaymentSuccessPage />,
  },
  {
    path: '/pago/error',
    element: <PaymentErrorPage />,
  },
  {
    path: '/pago/pendiente',
    element: <PaymentPendingPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'asistente',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'tenants',
        element: <TenantsPage />,
      },
      {
        path: 'plans',
        element: <PlansPage />,
      },
      {
        path: 'comercial',
        element: <ComercialLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="conexion" replace />,
          },
          {
            path: 'conexion',
            element: <CoofitConexionPage />,
          },
          {
            path: 'marketing',
            element: <CoofitMarketingPage />,
          },
        ],
      },
      {
        path: 'coofit-conexion',
        element: <Navigate to="/comercial/conexion" replace />,
      },
      {
        path: 'coofit-marketing',
        element: <Navigate to="/comercial/marketing" replace />,
      },
      {
        path: 'adicionales',
        element: <AdicionalesPage />,
      },
      {
        path: 'payments',
        element: <Navigate to="/panel-facturacion" replace />,
      },
      {
        path: 'panel-facturacion',
        element: <BillingCurrentPeriodPage />,
      },
      {
        path: 'panel-facturacion-historico',
        element: <BillingHistoryPage />,
      },
      {
        path: 'reports',
        element: <ReportsPage />,
      },
      {
        path: 'collections',
        element: <CollectionsPage />,
      },
      {
        path: 'facturas-clinicas',
        element: <ClinicInvoicesPage />,
      },
      {
        path: 'ecosystem-usage',
        element: <EcosystemUsagePage />,
      },
      {
        path: 'reminder-analytics',
        element: <ReminderAnalyticsPage />,
      },
      {
        path: 'period-ops',
        element: (
          <ProtectedRoute require="administer">
            <PeriodOpsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'balance',
        element: <Navigate to="/panel-facturacion" replace />,
      },
      {
        path: 'support',
        element: <SupportPage />,
      },
      {
        path: 'emails',
        element: <EmailsPage />,
      },
      {
        path: 'documentation',
        element: <DocumentationPage />,
      },
      {
        path: 'documentation/articles/:id',
        element: <DocumentationArticlePage />,
      },
      {
        path: 'solicitudes',
        element: <SolicitudesPage />,
      },
      {
        path: 'configuracion',
        element: <Navigate to="/configuracion-whatsapp" replace />,
      },
      {
        path: 'configuracion-app',
        element: <SettingsPage section="app" />,
      },
      {
        path: 'configuracion-periodo',
        element: (
          <ProtectedRoute require="administer">
            <PeriodSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'configuracion-whatsapp',
        element: <SettingsPage section="whatsapp" />,
      },
      {
        path: 'configuracion-email',
        element: <EmailTemplatesPage />,
      },
      {
        path: 'categorias-gastos',
        element: <ExpenseCategoriesPage />,
      },
      {
        path: 'configuracion-usuarios',
        element: (
          <ProtectedRoute require="manageUsers">
            <PanelUsersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'proyeccion-costos',
        element: <CostProjectionPage />,
      },
      {
        path: 'auditorias',
        element: (
          <ProtectedRoute require="administer">
            <AuditoriasPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'logs',
        element: (
          <ProtectedRoute require="administer">
            <LogsPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
